import { Plus } from "lucide-react";
import type { ReactNode } from "react";

import { Label } from "@/app/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/components/ui/popover";
import { cn } from "@/app/lib/utils";

interface SuggestionsPopoverProps {
	/** Names the editor this row sits above. Omitted where the section names itself. */
	label?: string;
	/** One line telling the reader what picking from the list does. */
	hint: string;
	/** Overrides the pill's wording where the list holds something other than phrases. */
	triggerLabel?: string;
	/** The list itself, plus any search that narrows it. */
	children: ReactNode;
}

/**
 * A section's label with its writing suggestions behind a popover.
 *
 * The suggestions used to hold a column of their own beside the editor, which
 * left both of them too narrow to read a sentence in and pushed the section's
 * buttons off the fold. A popover costs the page no width, so the editor gets all
 * of it and the list opens over the top only when it is asked for.
 */
const SuggestionsPopover = ({ label, hint, triggerLabel, children }: SuggestionsPopoverProps) => (
	<div className={cn("mb-1 flex items-center gap-2 sm:gap-3", label ? "justify-between" : "justify-start")}>
		{label && <Label className="text-sm">{label}</Label>}
		<Popover>
			<PopoverTrigger asChild>
				{/* `shrink-0` keeps the pill's own wording off a second line when the label
				    beside it is long enough to compete for the row. */}
				<button
					type="button"
					className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-primary-50 px-3 py-1.5 font-medium text-gray-600 text-sm"
				>
					{triggerLabel ?? "Pre-written phrases"}
					<span className="flex size-5 items-center justify-center rounded-full bg-primary-600 text-white">
						<Plus className="size-3.5" />
					</span>
				</button>
			</PopoverTrigger>
			{/* Bounded by the room the trigger leaves on screen, so the list scrolls
			    inside the popover rather than the popover growing off the top of it. */}
			<PopoverContent
				align="end"
				collisionPadding={16}
				className="flex max-h-(--radix-popover-content-available-height) w-[min(32rem,92vw)] flex-col p-2"
			>
				<p className="px-1 text-muted-foreground text-xs">{hint}</p>
				{children}
			</PopoverContent>
		</Popover>
	</div>
);

export default SuggestionsPopover;
